import microOrgs from '@/sui/data/microOrgs'
import assert from '@/sui/tools/assert'

function getCovid (pat) {
  return (pat.infrespi === 1 && pat.virus === 'VIRCOV') ? 2 : 3
}

function getOrigin (o) {
  switch (o) {
    case 1: return 1 // 'DOM'
    case 2: return 7 // 'EHP'
    case 3: return 6 // 'SLD'
    case 4: return 4 // 'SSR'
    case 5: return 3 // 'MCO'
    case 6: return 2 // 'REA'
  }

  return 9
}

function getMicroOrganisms (o, suffix, virus) {
  const ms = []
  const list = virus ? microOrgs.completeList.v2 : microOrgs.list.v2
  for (let i = 1; i < 3; i++) {
    const m = getMicroOrganism(o, o[`mo${i}${suffix}`], i, list)
    if (m != null) {
      ms.push(m)
    }
  }
  return ms
}

function getValue (v, good, neutral, corr) {
  assert(corr == null || good.length === corr.length)
  const idx = good.indexOf(v)
  if (idx !== -1) {
    if (corr != null) {
      return corr[idx]
    }
    return v
  }
  return neutral
}

function getYesNo (v) {
  return getValue(v, [1, 2], 9)
}

function getSSSFPRNCResist (v) {
  return getValue(v, [0, 1], 3, [0, 2])
}

function getONNCResist (v) {
  return getValue(v, [0, 1], 2, [1, 0])
}

function getFLUResist (v) {
  return getValue(v, [0, 1], 2)
}

const hasPanList = microOrgs.enterobacteria.slice()
hasPanList.push(
  'STA AUR', 'ENC FAE', 'ENC FAI', 'PSE AER', 'ACI BAU'
)

function hasPan (c) {
  return hasPanList.find(m => m === c) != null
}

function getMicroOrganism (o, code, n, list) {
  if (code == null || code.length === 0) {
    return null
  }

  let c = `${code.slice(0, 3)} ${code.slice(3, 6)}`
  if (c === 'ENC FAC') {
    c = 'ENC FAI'
  }
  const mc = list.find((m) => m.code === c)
  if (mc == null) {
    return null
  }

  const fields = [
    { name: 'oxa', code: 'oxa', convert: getSSSFPRNCResist },
    { name: 'gly', code: 'gly', convert: getSSSFPRNCResist },
    { name: 'caz', code: 'caz', convert: getSSSFPRNCResist },
    { name: 'c3g', code: 'c3g', convert: getSSSFPRNCResist },
    { name: 'car', code: 'car', convert: getSSSFPRNCResist },
    { name: 'flu', code: 'flu', convert: getFLUResist },
    { name: 'epc', code: 'epc', convert: getONNCResist },
    { name: 'blse', code: 'blse', convert: getONNCResist }
  ]

  const isEnterobacter = microOrgs.enterobacteria.find((m) => m === mc.code) != null
  const result = { code: mc.index, antibio: [] }

  let blse = false
  let epc = -1

  let shouldHaveBLSE = false
  let shouldHaveEPC = false

  for (let i = 0; i !== fields.length; ++i) {
    const f = fields[i]
    const value = o[`${f.name}${n}`]
    if (value == null || value === '') {
      continue
    }

    blse = f.code === 'blse'
    const nv = f.convert(value)
    if (nv == null) {
      continue
    }

    result.antibio.push(
      {
        code: microOrgs.antibioCode.v2[f.code],
        resistance: nv
      }
    )

    if (nv === 2) {
      if (f.code === 'car') {
        if (mc.index === 61) {
          result.antibio.push({ code: 11, resistance: 2 })
        } else if (isEnterobacter) {
          shouldHaveEPC = true
        }
      } else if (f.code === 'c3g' && isEnterobacter) {
        shouldHaveBLSE = true
      }
    }

    if (f.code === 'epc') {
      epc = nv
    }
  }

  if (hasPan(mc.code)) {
    result.antibio.push({ code: 5, resistance: 3 })
  }

  if (!blse && shouldHaveBLSE) {
    result.antibio.push({ code: 7, resistance: 2 })
  }

  if (shouldHaveEPC) {
    if (epc === -1) {
      result.antibio.push({ code: 8, resistance: 2 })
    } else if (epc === 0) {
      result.antibio.push({ code: 9, resistance: 9 })
    }
  }

  return result
}

function getSeverity (o) {
  return { 'code': 'igsII', 'value': o }
}

function getPortage (sarm, gisa, erg, eblse, epc, abri, parc) {
  const list = [
    sarm, gisa, erg, eblse, epc, abri, parc
  ]

  let nc = true
  for (let i = 0; i !== list.length; ++i) {
    const mo = list[i]
    if (mo === 1) {
      return 1
    }

    nc = nc && mo === 9
  }
  return nc ? 9 : 2
}

function getPortageBmrBhre (sarm, gisa, erg, eblse, epc, abri, parc) {
  // By priority
  const portages = [
    { in: epc, out: 6 },
    { in: erg, out: 3 },
    { in: sarm, out: 1 },
    { in: eblse, out: 5 },
    { in: gisa, out: 2 },
    { in: abri, out: 7 },
    { in: parc, out: 8 }
  ]

  const res = []
  for (let i in portages) {
    const p = portages[i]
    if (p.in === 1) {
      res.push({ 'code': p.out })
    }
  }
  return res
}

function getDeathDate (p) {
  if (p.etatsort === 1) {
    return p.dsortie
  }
  return null
}

function getExitDate (p) {
  if (p.etatsort !== 1) {
    return p.dsortie
  }
  return null
}

function getRemovalDate (fincvc, labo) {
  if (labo === 1 || labo === 2) {
    return fincvc
  }
  return null
}

function getEntrance (entrance) {
  switch (entrance) {
    case 1:
      return 'C13'
    case 2:
      return 'C5'
    case 3:
      return 'C1'
    case 4:
      return 'C4'
    case 5:
      return 'D1'
    case 6:
      return 'C2'
    case 7:
      return 'C6'
    case 8:
      return 'C12'
    case 9:
      return 'C7'
    case 10:
      return 'A3'
    case 11:
      return 'A4'
    case 12:
      return 'A5'
    case 13:
      return 'A10'
    case 14:
      return 'A1'
    case 15:
      return 'A12'
  }
  return 'A13'
}

function addEntranceKTFields (result) {
  const e = result.entrance_v2
  if (e == null || e === '') {
    return
  }
  if (e.substring(0, 1) === 'C' || e === 'D1') {
    result.insertionSite = 9
    result.removed = 9
  } else if (e === 'A13') {
    result.catheter = 9
  }
}

function newDate (date) {
  if (date == null || date === '') {
    return null
  }

  const values = date.split('/')
  const day = parseInt(values[0], 10)
  const month = parseInt(values[1], 10)
  const year = parseInt(values[2], 10)
  return new Date(year, month - 1, day, 10)
}

function getBmi (imc) {
  assert.isNumber(imc)
  if (imc < 1 || imc > 98.9) {
    return 999
  }

  const intImc = imc >= 98.5 ? parseInt(imc) : Math.round(imc)
  assert.isInteger(intImc)
  return intImc
}

function createPatient (p) {
  const mandatoryFields = [
    'codetab', 'codserv', 'fiche', 'dnais', 'dentree', 'dsortie',
    'etatsort', 'sexe', 'atbadm', 'trauma', 'catediag', 'provpat', 'idep',
    'igsII', 'typebmrsarm', 'typebmrgisa', 'typebmrerg', 'typebmreblse',
    'typebmrepc', 'typebmrabri', 'typebmrparc', 'intub', 'cvc', 'imc'
  ]
  const errors = []
  mandatoryFields.forEach(
    (f) => {
      if (p[f] == null || p[f] === '') {
        errors.push(`missing field ${f}`)
      }
    }
  )
  if (errors.length > 0) {
    throw new Error(errors.join(', '))
  }

  return {
    rezoId: `${p.fiche}.${p.codetab}.${p.codserv}`,
    codserv: p.codserv,
    sheetNumber: p.codserv * 1000 + p.fiche,
    birthdate: newDate(p.dnais),
    sex: getValue(p.sexe, [1, 2], 9),
    entry: newDate(p.dentree),
    origin: getOrigin(p.provpat),
    trauma: getYesNo(p.trauma),
    diagnosis: getValue(p.catediag, [1, 2, 3], 9),
    immuno: getValue(p.idep, [1, 2, 3], 9),
    cancer: 9,
    antibio: getYesNo(p.atbadm),
    mainSeverity: getSeverity(p.igsII),
    bmi: getBmi(p.imc),
    portageBmrBhre: getPortage(
      p.typebmrsarm,
      p.typebmrgisa,
      p.typebmrerg,
      p.typebmreblse,
      p.typebmrepc,
      p.typebmrabri,
      p.typebmrparc
    ),
    bmrBhreMicroOrgs: getPortageBmrBhre(
      p.typebmrsarm,
      p.typebmrgisa,
      p.typebmrerg,
      p.typebmreblse,
      p.typebmrepc,
      p.typebmrabri,
      p.typebmrparc
    ),
    ecmo: getValue(p.ecmo, [1, 2, 3], 9),
    intubated: getYesNo(p.intub),
    intubStart: newDate(p.debutintub),
    reintubated: getYesNo(p.reint),
    intubEnd: newDate(p.finintub),
    urinary: 9,
    urinaryStart: newDate(p.debutsad),
    urinaryEnd: newDate(p.finsad),
    dead: getYesNo(p.etatsort),
    death: newDate(getDeathDate(p)),
    exit: newDate(getExitDate(p)),
    covidStatus: getCovid(p),
    catheters: [],
    bacteremias: [],
    pneumopathies: []
  }
}

function getRemoved (labo) {
  switch (labo) {
    case 1:
    case 2:
      return 1
    case 9:
      return 9
  }
  return 2
}

function getCVCType (t) {
  switch (t) {
    case 1: return 'C1'
    case 2: return 'D1'
    case 3: return 'C4'
    case 4: return 'C8'
  }
  return 9
}

function getInsertionSite (s) {
  switch (s) {
    case 1: return 1
    case 2: return 2
    case 3: return 3
    case 4: return 7
    case 5: return 10
  }
  return 9
}

function createCVC (c) {
  const mandatoryFields = [
    'codetab', 'codserv', 'fiche', 'numcvc', 'typecvc', 'sitecvc', 'debutcvc',
    'fincvc', 'labo'
  ]
  const errors = []
  mandatoryFields.forEach(
    (f) => {
      if (c[f] == null || c[f] === '') {
        errors.push(`Missing field ${f}`)
      }
    }
  )
  if (errors.length > 0) {
    throw new Error(errors.join('\n'))
  }

  const removed = getRemoved(c.labo)
  return {
    rezoId: `${c.fiche}.${c.codetab}.${c.codserv}`,
    central: 1,
    number: c.numcvc,
    type_v2: getCVCType(c.typecvc),
    insertion: newDate(c.debutcvc),
    insertionSite: getInsertionSite(c.sitecvc),
    removed: removed,
    removal: newDate(getRemovalDate(c.fincvc, c.labo)),
    laboratory: removed === 1 ? getValue(c.labo, [1, 2, 3], 9) : null,
    isColIlcBlc: getValue(c.colinf, [0, 1, 2, 3, 4], 9),
    colIlcBlc: newDate(c.datecolinf),
    lipids: 9,
    divDiag: 9,
    microOrganisms: getMicroOrganisms(c, 'colinf', false)
  }
}

function createBacteriemia (i) {
  const mandatoryFields = [
    'codetab', 'codserv', 'fiche', 'numinf', 'datein', 'sitein', 'mo1in', 'pein'
  ]
  const errors = []
  mandatoryFields.forEach(
    (f) => {
      if (i[f] == null || i[f] === '') {
        errors.push(`Missing field ${f}`)
      }
    }
  )
  if (errors.length > 0) {
    throw new Error(errors.join('\n'))
  }
  const result = {
    rezoId: `${i.fiche}.${i.codetab}.${i.codserv}`,
    present: 1,
    episod: newDate(i.datein),
    covidStatus: 9,
    divDiag: 9,
    microOrganisms: getMicroOrganisms(i, 'in', false),
    entrance_v2: getEntrance(i.pein),
    previousManip: 9
  }
  addEntranceKTFields(result)
  return result
}

function createPneumopathy (i) {
  const mandatoryFields = [
    'codetab', 'codserv', 'fiche', 'numinf', 'datein', 'sitein', 'mo1in',
    'cdpin'
  ]
  const errors = []
  mandatoryFields.forEach(
    (f) => {
      if (i[f] == null || i[f] === '') {
        errors.push(`Missing field ${f}`)
      }
    }
  )
  if (errors.length > 0) {
    throw new Error(errors.join('\n'))
  }
  return {
    rezoId: `${i.fiche}.${i.codetab}.${i.codserv}`,
    pneumopathy: 1,
    episod: newDate(i.datein),
    diagnosis: getValue(i.cdpin, [1, 2, 3, 4, 5], 9),
    covidStatus: 9,
    microOrganisms: getMicroOrganisms(i, 'in', true)
  }
}

export default {
  createPatient: createPatient,
  createCVC: createCVC,
  createBacteriemia: createBacteriemia,
  createPneumopathy: createPneumopathy
}
